import * as React from 'react';
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid';
import { Avatar } from '@mui/material';
import Colors from '../../constants/Colors';
import { gql } from '@apollo/client';
import { getNameAvatar } from '../../utils/functions';
import { gBaseUrl } from '../../utils/config';
import { useGetAllUsersByPage } from '../../hooks/gql/query/getAll/useGetAllUsersByPage';
import { useUpdateUser } from '../../hooks/gql/mutation/update/useUpdateUser';
import ActionButtonsPause from '../../components/Utils/ActionsButtonsPause';

const companyTableState = {
  initialState: {
    columns: {
      columnVisibilityModel: {
        id: false,
        company_name: true,
        phone: true,
        email: true,
        action: true,
      },
    },
  },
};

const companyTableColumn: GridColDef[] = [
  { field: 'id', headerClassName: 'company_table_header' },
  {
    field: 'phone',
    headerName: 'Phone Number',
    minWidth: 150,
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    headerClassName: 'company_table_header',
  },

  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    minWidth: 200,
    headerAlign: 'center',
    headerClassName: 'company_table_header',
  },
];

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: '#F4F6F6',
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}));

const GET_ALL_USERS = gql`
  query getAllUsersByPage($input: PaginationInput!) {
    getAllUsersByPage(input: $input) {
      _id
      company_name
      photo
      isPhotoVisible
      phone
      email
      isSuspended
      isPaused
      vehicle_type
    }
  }
`;

const CompaniesPausedPage: React.FC<{}> = (props) => {
  const { updateUser } = useUpdateUser();

  const {
    data: users,
    error: usersError,
    loading: usersLoading,
    refetch: usersRefectch,
  } = useGetAllUsersByPage(GET_ALL_USERS, { page: 1, item_per_page: 1000 });

  const users_row =
    users?.map((c) => {
      return { ...c, id: c._id, actions: 'V' };
    }) || [];

  return (
    <>
      <div style={{ height: 631, width: '100%', marginBottom: 150 }}>
        <StripedDataGrid
          pageSizeOptions={[10, 50, 100]}
          columns={[
            {
              field: 'company_name',
              headerName: 'Company Name',
              minWidth: 150,
              maxWidth: 200,
              flex: 1,
              headerAlign: 'center',
              headerClassName: 'company_table_header',
              renderCell: (params) => {
                const data = params.row;
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    {data?.isPhotoVisible ? (
                      <Avatar
                        sizes='s'
                        sx={{
                          bgcolor: Colors.primaryColorLight,
                          marginRight: 1,
                          width: 27,
                          height: 27,
                        }}
                        src={`${gBaseUrl}/photos/avatars/${data?.photo}`}
                      />
                    ) : (
                      <Avatar
                        sizes='s'
                        sx={{
                          bgcolor: Colors.primaryColorLight,
                          marginRight: 1,
                          width: 27,
                          height: 27,
                        }}>
                        <p style={{ fontSize: 12 }}>
                          {getNameAvatar(
                            data?.company_name || 'Filling Information'
                          )}
                        </p>
                      </Avatar>
                    )}
                    <p>{data?.company_name || 'Filling Information'}</p>
                  </div>
                );
              },
            },
            ...companyTableColumn,
            {
              field: 'action',
              headerName: 'Pause Operator',
              maxWidth: 120,
              flex: 1,
              headerAlign: 'center',
              align: 'center',
              sortable: false,
              filterable: false,
              headerClassName: 'company_table_header',
              renderCell: (params) => {
                const data = params.row;
                return (
                  <div>
                    <ActionButtonsPause
                      id={data.id}
                      isPaused={data.isPaused}
                      onSwitchClick={async (_id, isPaused) => {
                        await updateUser({
                          _id,
                          isPaused,
                        });
                        usersRefectch();
                      }}
                    />
                  </div>
                );
              },
            },
          ]}
          rows={users_row}
          initialState={{
            ...companyTableState.initialState,
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pagination
          sx={{
            '& .company_table_header': {
              backgroundColor: '#F3F8FF',
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
        />
      </div>
    </>
  );
};
export default CompaniesPausedPage;
